import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { roleAPI } from '../../utils/api';

export default function RoleCreate({ role = null, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!role;

  useEffect(() => {
    if (role) setForm({ name: role.name || '', description: role.description || '' });
  }, [role]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Role name is required';
    else if (!/^[A-Z0-9_]+$/.test(form.name.trim())) e.name = 'Use UPPER_CASE format (e.g. ROLE_ADMIN)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'name' ? value.toUpperCase() : value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (isEdit) await roleAPI.updateRole(role.id, form);
      else await roleAPI.createRole(form);
      Swal.fire({ title: 'Success', text: `Role ${isEdit ? 'updated' : 'created'} successfully`, icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      onSaved?.();
      onClose?.();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={fieldGroup}>
        <label style={labelStyle}>Role Name <span style={{ color: '#e74c3c' }}>*</span></label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g. ROLE_PROCUREMENT_OFFICER"
          disabled={submitting}
          style={{ ...inputStyle, ...(errors.name ? errorBorder : {}) }}
        />
        {errors.name && <span style={errorText}>{errors.name}</span>}
      </div>

      <div style={fieldGroup}>
        <label style={labelStyle}>Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={3}
          disabled={submitting}
          placeholder="Describe this role's purpose (optional)"
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="button" onClick={onClose} disabled={submitting} style={ghostBtn}>Cancel</button>
        <button type="submit" disabled={submitting} style={primaryBtn}>
          {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Role'}
        </button>
      </div>
    </form>
  );
}

RoleCreate.propTypes = {
  role: PropTypes.object,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
};

const fieldGroup  = { marginBottom: 16 };
const labelStyle  = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle  = { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#f8fafc', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif", boxSizing: 'border-box', transition: 'border-color 0.2s' };
const errorBorder = { borderColor: '#e74c3c', background: 'rgba(231,76,60,0.03)' };
const errorText   = { fontSize: '0.75rem', color: '#e74c3c', marginTop: 4, display: 'block' };
const primaryBtn  = { flex: 1, padding: '10px', borderRadius: 10, background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff', border: 'none', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif" };
const ghostBtn    = { flex: 1, padding: '10px', borderRadius: 10, background: '#f1f5f9', color: '#374151', border: 'none', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif" };
